import { buildVideoStudioFormData } from "./generateVideo";
import { mediaSrc } from "../../../utils/mediaSrc";
import { resolveStitchVoiceLock } from "../klingVoiceDefaults";
import { modelSupportsKlingVoiceover } from "../modelProfiles";
import { sceneDurationsFromForm } from "../scenePlan";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function isServerUsableUrl(url) {
  const s = String(url || "").trim();
  if (!s) return false;
  if (s.startsWith("blob:")) return false;
  return /^https?:\/\//i.test(s) || s.startsWith("/media/");
}

function readApiError(res, fallback) {
  if (res?.logout) return "Session expired. Please sign in again.";
  return res?.data?.msg || res?.error || fallback;
}

/**
 * Start a server-side long-video orchestration run and poll until complete.
 * Flow: plan segments → generate → last-frame continue → stitch (+ optional voice).
 *
 * @param {Function} hitAxios
 * @param {object} form
 * @param {string} categoryId
 * @param {{
 *   onProgress?: (info: { phase: string; segment?: number; total?: number; status?: string }) => void;
 *   onSegmentReady?: (info: { segment: number; total: number; clip: object }) => void;
 *   onSegmentStart?: (info: { segment: number; total: number }) => void;
 *   onVoiceLocked?: (info: { id: string; label: string; autoLocked?: boolean }) => void;
 *   clearBoard?: () => void;
 *   creditsBalance?: number|string;
 * }} [opts]
 */
export async function runServerOrchestratedGeneration(hitAxios, form, categoryId, opts = {}) {
  const segments = sceneDurationsFromForm(form);
  const total = segments.length;
  const modelLabel = form.model || "Video";
  const klingVoice = modelSupportsKlingVoiceover(form.model);
  const voiceLock = klingVoice ? resolveStitchVoiceLock(form) : null;

  if (voiceLock?.mode === "preset" && voiceLock.id) {
    opts.onVoiceLocked?.({
      id: voiceLock.id,
      label: voiceLock.label || voiceLock.id,
      autoLocked: false,
    });
  }

  opts.clearBoard?.();

  const fd = buildVideoStudioFormData(form, categoryId);
  fd.append("targetDuration", String(Math.round(Number(form.duration) || 0)));

  if (Array.isArray(form.scenes) && form.scenes.length > 1) {
    // Serialize scene-level media URLs so server orchestration can preserve
    // per-scene references across polling/restarts.
    fd.append(
      "segmentPlan",
      JSON.stringify(
        form.scenes.map((s) => {
          const images = (s?.imageUrlUploads || []);
          const imageUrls = images
            .map((u) => u?.previewUrl)
            .filter(isServerUsableUrl)
            .slice(0, 5);
          const referenceAudioUrls = images
            .map((u) => u?.voiceClip?.previewUrl)
            .filter(isServerUsableUrl)
            .slice(0, 5);
          return {
            duration: Number(s.duration) || undefined,
            prompt: String(s.prompt || "").trim() || undefined,
            imageUrls: imageUrls.length ? imageUrls : undefined,
            referenceAudioUrls: referenceAudioUrls.length
              ? referenceAudioUrls
              : undefined,
            characterVoiceId:
              String(images[0]?.voiceClip?.voiceId || "").trim() || undefined,
            speaker: String(s.speaker || "").trim() || undefined,
          };
        }),
      ),
    );

    // Upload scene-local files once so backend orchestrator can reuse them
    // when generating later segments.
    form.scenes.forEach((s, sceneIdx) => {
      (s?.imageUrlUploads || []).forEach((u, fileIdx) => {
        if (u?.file instanceof File) {
          fd.append(`sceneImageUploads_${sceneIdx}_${fileIdx}`, u.file);
        }
        if (u?.voiceClip?.file instanceof File) {
          fd.append(`sceneVoiceUploads_${sceneIdx}_${fileIdx}`, u.voiceClip.file);
        }
      });
    });
  }

  const hasCharacterVoice =
    !form.multiSpeakerDialogue &&
    ((form.imageUrlUploads || []).some((u) => u?.voiceClip?.file || u?.voiceClip?.previewUrl) ||
      (Array.isArray(form.scenes) &&
        form.scenes.some((s) =>
          (s?.imageUrlUploads || []).some((u) => u?.voiceClip?.file || u?.voiceClip?.previewUrl),
        )));

  const shouldUnify =
    !form.multiSpeakerDialogue &&
    ((klingVoice &&
      form.sound !== false &&
      voiceLock &&
      (voiceLock.mode === "ai" ||
        voiceLock.mode === "upload-seed" ||
        (voiceLock.mode === "preset" && voiceLock.id))) ||
      hasCharacterVoice);

  if (shouldUnify) {
    fd.append("unifyVoice", "true");
    if (klingVoice && voiceLock?.mode === "preset" && voiceLock.id) {
      fd.append("voiceId", String(voiceLock.id));
    }
    if (klingVoice && voiceLock?.mode === "upload-seed") {
      const seed =
        (form.referenceAudioUploads || []).find((u) => u?.previewUrl)?.previewUrl ||
        (form.imageUrlUploads || []).find((u) => u?.voiceClip?.previewUrl)?.voiceClip
          ?.previewUrl;
      if (seed && !String(seed).startsWith("blob:")) fd.append("seedAudioUrl", String(seed));
    }
  }

  if (form.multiSpeakerDialogue) {
    fd.append("multiSpeakerDialogue", "true");
  }

  if (form.resolution) fd.append("resolution", String(form.resolution));
  if (form.hailuoResolution) fd.append("hailuoResolution", String(form.hailuoResolution));

  const startRes = await hitAxios({
    path: "/api/video-studio/orchestrate",
    post: true,
    obj: fd,
    showLoading: false,
    showSnackbar: false,
    timeout: 120000,
  });

  if (!startRes?.data?.success) {
    throw new Error(readApiError(startRes, "Could not start long-video orchestration."));
  }

  let run = startRes.data.data?.run;
  if (!run?.id) {
    throw new Error("Server did not return an orchestration run id.");
  }

  const seenReady = new Set();
  const boardBySeg = new Map();

  const notifyProgress = (r) => {
    const phase = r?.phase || r?.progress?.phase || r?.status;
    const seg = r?.progress?.segment ?? r?.current_segment;
    const tot = r?.segment_total || total;
    if (phase === "segment" || r?.status === "generating") {
      opts.onSegmentStart?.({ segment: seg || 1, total: tot });
      opts.onProgress?.({ phase: "generating", segment: seg, total: tot, status: r.status });
    } else if (phase === "stitch" || r?.status === "stitching") {
      opts.onProgress?.({ phase: "stitching", total: tot, status: r.status });
    } else if (phase === "voice" || r?.status === "voice") {
      opts.onProgress?.({ phase: "voiceover", total: tot, status: r.status });
    } else {
      opts.onProgress?.({ phase: phase || r?.status, segment: seg, total: tot, status: r.status });
    }

    for (const clip of r?.clips || []) {
      const seg1 = Number(clip.segment);
      if (!seg1 || !clip.url || seenReady.has(seg1)) continue;
      seenReady.add(seg1);
      const boardClip = {
        url: mediaSrc(clip.url) || clip.url,
        duration: clip.duration || segments[seg1 - 1] || 5,
        modelId: form.model,
        modelLabel,
        aspectRatio: form.aspectRatio || null,
        outputResolution: form.resolution || null,
        jobId: clip.job_id ?? null,
        thumbnailUrl: mediaSrc(clip.url) || clip.url,
        segmentIndex: seg1,
        segmentTotal: tot,
        status: "ready",
      };
      boardBySeg.set(seg1, boardClip);
      opts.onSegmentReady?.({ segment: seg1, total: tot, clip: boardClip });
    }
  };

  notifyProgress(run);

  const maxAttempts = 240;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (run.status === "completed") {
      const boardClips = [...boardBySeg.values()].sort(
        (a, b) => Number(a.segmentIndex) - Number(b.segmentIndex),
      );
      return {
        videoUrl: mediaSrc(run.video_url) || run.video_url,
        jobId: run.final_job_id || null,
        segmentCount: run.segment_total || total,
        segmentDurations: run.segment_durations || segments,
        boardClips,
        run,
      };
    }
    if (run.status === "failed") {
      throw new Error(run.error_message || "Long-video orchestration failed.");
    }

    await sleep(attempt < 5 ? 3500 : 5000);

    const pollRes = await hitAxios({
      path: `/api/video-studio/orchestrate/${run.id}/poll`,
      post: true,
      showLoading: false,
      showSnackbar: false,
    });

    if (!pollRes?.data?.success) {
      throw new Error(readApiError(pollRes, "Could not poll orchestration run."));
    }

    run = pollRes.data.data?.run;
    if (!run) throw new Error("Invalid orchestration poll response.");
    notifyProgress(run);
  }

  throw new Error("Long-video orchestration timed out.");
}
